import { useEffect, useState, useRef } from 'react';
import { dequal } from 'dequal';
import { IconPlus, IconInfoCircle } from '@tabler/icons-react';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import useTranslation from '@/hooks/useTranslation';
import useWorkspaceRoles from '@/hooks/useWorkspaceRoles';
import useCreateProjectStore from '@/store/CreateProjectStore';
import ScreeningQuestionBox from './components/ScreeningQuestionBox';
import { ScreeningQuestionType, ScreeningRulesTypes } from '@/enums';
import { Media, ScreeningProps, ScreeningQuestion, ScreeningResponse } from '@/types';
import { applyServerOwnedMedia } from '@/utils/questionGroupHelpers';
import { buildScreeningDeletionPayload } from '@/utils/screeningQuestionHelpers';
import ToolTip from '@/components/tooltip/ToolTip';
import useScreeningQuestionsStore from '@/store/ScreeningQuestionsStore';

const singleSelectDefaultAnswers = [
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
];

const multiSelectDefaultAnswers = [
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
  { id: uuidv4(), value: '', rule: null },
];

const numericDefaultAnswers = {};

const MAX_QUESTIONS = 25;

function ScreeningQuestions({
  isProjectPublished,
  isProjectCompleted,
  setValue,
  onSubmit,
  screeningQuestions,
  register,
  errors,
  clearErrors,
}: ScreeningProps) {
  const { lang } = useTranslation();
  const [questions, setQuestions] = useState<ScreeningQuestion[]>(screeningQuestions || []);
  const [isActiveDrag, setIsActiveDrag] = useState(false);
  const [openQuestionId, setOpenQuestionId] = useState<string | null>(null);
  const draftQuestionsRef = useRef<ScreeningQuestion[]>([]);
  const { isUserViewer } = useWorkspaceRoles();
  const { setScreeningQuestionsLength } = useScreeningQuestionsStore();
  const {
    setReorderChanged,
    setIsFormChanged,
    setDuplicatedScreeningQuestion,
    setDuplicatedScreeningQuestionAnswer,
    isFormChanged,
  } = useCreateProjectStore();

  // INVARIANT — this refreshes the CONTENT of existing draft entries only; it
  // neither adds nor removes ids. Membership of `draftQuestionsRef` is owned by
  // three places: `handleAddNewQuestion` (adds, before calling this),
  // `handleDeleteQuestion` (removes), and the `[screeningQuestions]` effect
  // (removes once the server knows the question). A caller that introduces a
  // brand-new question without registering it there first would leave it
  // unregistered — and since the deletion payload treats "not a draft" as
  // "already saved", it would then be sent to the server as if it were.
  const updateQuestions = (updatedQuestions: ScreeningQuestion[]) => {
    setQuestions(updatedQuestions);
    draftQuestionsRef.current = draftQuestionsRef.current.map(
      (q) => updatedQuestions.find((updatedQ) => updatedQ.id === q.id) || q,
    );
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });
  };

  const updateQuestionById = (
    questionId: string,
    updater: (question: ScreeningQuestion) => ScreeningQuestion,
  ) => {
    const updatedQuestions = questions.map((question) =>
      question.id === questionId ? updater(question) : question,
    );
    updateQuestions(updatedQuestions);
  };

  const getResponsesForType = (newType: ScreeningQuestionType) => {
    const responseActions = {
      [ScreeningQuestionType.SINGLE_SELECT]: singleSelectDefaultAnswers,
      [ScreeningQuestionType.MULTI_SELECT]: multiSelectDefaultAnswers,
    };

    return responseActions[newType as keyof typeof responseActions] || [];
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setQuestions((prevQuestions) => {
      const oldIndex = prevQuestions.findIndex((q) => q.id === active.id);
      const newIndex = prevQuestions.findIndex((q) => q.id === over.id);

      const reorderedQuestions = arrayMove(prevQuestions, oldIndex, newIndex);
      setValue('screening_questions', reorderedQuestions, { shouldDirty: true });
      setReorderChanged(true);
      return reorderedQuestions;
    });

    setIsActiveDrag(false);
  };

  const handleAddNewQuestion = () => {
    const newQuestion: ScreeningQuestion = {
      id: uuidv4(),
      question: '',
      type: ScreeningQuestionType.MULTI_SELECT,
      responses: [...multiSelectDefaultAnswers],
    };

    draftQuestionsRef.current = [...draftQuestionsRef.current, newQuestion];
    updateQuestions([...(questions || []), newQuestion]);
    setOpenQuestionId(newQuestion.id);
  };

  const handleDuplicatedQuestions = (newValue: string, questionId: string) => {
    if (!newValue) return;

    const isDuplicate = questions.some(
      ({ question }) => question.toLowerCase() === newValue.toLowerCase(),
    );

    setDuplicatedScreeningQuestion(isDuplicate ? { id: questionId } : { id: '' });
  };

  const handleDuplicatedAnswers = (
    newValue: string,
    answerId: string,
    responses: ScreeningResponse[],
  ) => {
    if (!newValue) return;
    const isDuplicate = responses.some(({ value }) => {
      return value.toLowerCase() === newValue.toLowerCase();
    });
    setDuplicatedScreeningQuestionAnswer(isDuplicate ? { id: answerId } : { id: '' });
  };

  const handleQuestionTextChange = (questionId: string, newValue: string) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    if (questionIndex >= 0) {
      clearErrors(`screening_questions.${questionIndex}.question`);
    }
    if (isFormChanged) handleDuplicatedQuestions(newValue, questionId);
    updateQuestionById(questionId, (question) => ({ ...question, question: newValue }));
  };

  const handleAnswersOrderChange = (questionId: string, answers: ScreeningResponse[]) => {
    updateQuestionById(questionId, (question) => ({
      ...question,
      responses: answers,
    }));
  };

  // Media (stimuli) label/reorder edits from QuestionMediaUploading. Routed
  // through the local questions state (the editor's source of truth) rather
  // than a direct RHF setValue — the [questions] sync effect below rewrites
  // the whole screening_questions form value from local state, so a
  // form-only write would be clobbered by the next unrelated edit. TELV1-520
  const handleQuestionMediaChange = (questionId: string, media: Media[]) => {
    updateQuestionById(questionId, (question) => ({
      ...question,
      media,
    }));
  };

  const onRandomizeAnswersChange = (questionId: string, randomize: boolean) => {
    updateQuestionById(questionId, (question) => ({
      ...question,
      randomize,
    }));
    setValue(
      `screening_questions.${questions.findIndex((q) => q.id === questionId)}.randomize`,
      randomize,
      {
        shouldDirty: true,
      },
    );
  };

  const onQuotasEnabledChange = (questionId: string, enabled: boolean) => {
    setIsFormChanged(true);
    updateQuestionById(questionId, (question) => {
      const updatedResponses = enabled
        ? question.responses
        : question.responses?.map((r) => ({ ...r, quota_percentage: undefined }));
      return {
        ...question,
        quotas_enabled: enabled,
        responses: updatedResponses,
      };
    });
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    setValue(`screening_questions.${questionIndex}.quotas_enabled`, enabled, {
      shouldDirty: true,
    });
  };

  const onQuotaPercentageChange = (
    questionId: string,
    answerId: string,
    percentage: number | undefined,
  ) => {
    setIsFormChanged(true);
    updateQuestionById(questionId, (question) => ({
      ...question,
      responses: question.responses?.map((r) =>
        r.id === answerId ? { ...r, quota_percentage: percentage } : r,
      ),
    }));
  };

  const handleQuestionTypeChange = (questionId: string, newType: ScreeningQuestionType) => {
    // Rebuild the answer config from scratch for the new type, but preserve
    // type-independent content — the question text and its media (stimuli).
    // Media lives on the server the moment it's uploaded; dropping it here
    // would silently orphan the uploaded files on the next save (TELV1-520).
    updateQuestionById(questionId, ({ id, question, media }) => ({
      id,
      question,
      ...(media ? { media } : {}),
      type: newType,
      quotas_enabled: false,
      ...(newType === ScreeningQuestionType.NUMERIC
        ? { numericResponse: numericDefaultAnswers }
        : { responses: getResponsesForType(newType) }),
    }));
  };

  const clearAnswerError = (
    questionId: string,
    answerId: string,
    field: 'value' | 'rule' | 'numericResponse',
  ) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const responseIndex = questions[questionIndex]?.responses?.findIndex((r) => r.id === answerId);

    if (questionIndex >= 0) {
      if (field === 'numericResponse') {
        clearErrors(`screening_questions.${questionIndex}.numericResponse`);
      } else if (typeof responseIndex === 'number' && responseIndex >= 0) {
        clearErrors(`screening_questions.${questionIndex}.responses.${responseIndex}.${field}`);
      }
    }
  };

  const handleQuestionAnswerChange = (
    questionId: string,
    answerId: string,
    newText: string | { minimum?: number; maximum?: number },
    newRule?: ScreeningRulesTypes | null,
  ) => {
    const questionIndex = questions.findIndex((q) => q.id === questionId);
    const responseIndex = questions[questionIndex]?.responses?.findIndex((r) => r.id === answerId);

    updateQuestionById(questionId, (question) => {
      if (question.type === ScreeningQuestionType.NUMERIC) {
        return {
          ...question,
          numericResponse: {
            ...question.numericResponse,
            ...(typeof newText !== 'string' &&
              newText.minimum !== undefined && { minimum: +newText.minimum }),
            ...(typeof newText !== 'string' &&
              newText.maximum !== undefined && { maximum: +newText.maximum }),
          },
        };
      }
      // For non-numeric types
      const responses = question.responses || [];
      const responseExists = responses.some((response) => response.id === answerId);

      const answer =
        typeof responseIndex === 'number'
          ? questions[questionIndex]?.responses?.[responseIndex]
          : undefined;
      const hasAnswerChanged = answer?.value !== newText;
      if (isFormChanged && hasAnswerChanged) {
        handleDuplicatedAnswers(newText as string, answerId, responses);
      }
      const updatedQuestion = {
        ...question,
        responses: responseExists
          ? responses.map((response) =>
              response.id === answerId
                ? {
                    ...response,
                    value: newText as string,
                    rule: newRule,
                  }
                : response,
            )
          : [
              ...responses,
              {
                id: answerId,
                rule: newRule,
                value: newText as string,
              },
            ],
      };

      setValue(`screening_questions.${questionIndex}.responses.${responseIndex}.rule`, newRule, {
        shouldDirty: true,
      });

      return updatedQuestion;
    });
  };

  const handleDeleteAnswer = (questionId: string, answerId: string) => {
    setIsFormChanged(true);
    const questionIndex = questions.findIndex((q) => q.id === questionId);

    let screeningResponse: ScreeningResponse[] = [];
    const updatedQuestions = questions.map((question) => {
      if (question.id === questionId) {
        screeningResponse = (question.responses || []).filter(
          (response) => response.id !== answerId,
        );
        return {
          ...question,
          responses: screeningResponse,
        };
      }
      return question;
    });

    clearErrors(`screening_questions.${questionIndex}.responses`);

    setQuestions(updatedQuestions);
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });

    if (
      questions.some((q) => {
        if (q.id === questionId && q.type !== ScreeningQuestionType.NUMERIC) {
          const responses = q.responses || [];
          return responses.length < 2;
        }
        return false;
      })
    ) {
      setIsFormChanged(false);
    }
  };

  const handleDeleteQuestion = (idToDelete: string) => {
    const isQuestionFromDraft = !screeningQuestions?.some(
      (q: { id: string }) => q.id === idToDelete,
    );
    draftQuestionsRef.current = draftQuestionsRef.current.filter((q) => q.id !== idToDelete);
    const questionIndex = questions.findIndex((q) => q.id === idToDelete);
    clearErrors(`screening_questions.${questionIndex}`);
    const updatedQuestions = questions.filter((question) => question.id !== idToDelete);
    setQuestions(updatedQuestions);
    setValue('screening_questions', updatedQuestions, { shouldDirty: true });
    if (!isQuestionFromDraft) {
      // Note: onSubmit's deletion-only path still runs the shared payload
      // sanitizers (pruneNoIntentScreeningAnswers + sanitize/strip in
      // ProjectCreate.onSubmit) — this does NOT bypass them.
      // Built from the LIVE editor list, not the server prop: the prop only
      // catches up when the post-save refetch lands, so a second deletion
      // fired before then used to resurrect the first one. See
      // buildScreeningDeletionPayload for why surviving questions still go
      // out with their SERVER content.
      const remainingScreeningQuestions = buildScreeningDeletionPayload(
        updatedQuestions,
        screeningQuestions,
        new Set(draftQuestionsRef.current.map((q) => q.id)),
      );
      onSubmit({ screening_questions: remainingScreeningQuestions }, true);
    }
  };

  useEffect(() => {
    setValue('screening_questions', questions, { shouldDirty: true });
  }, [questions, setValue]);

  useEffect(() => {
    if (questions && screeningQuestions) {
      const localQuestions = questions.filter(
        (q) => !screeningQuestions.some((dbQ: { id: string }) => dbQ.id === q.id),
      );
      draftQuestionsRef.current = localQuestions;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (screeningQuestions) {
      // A draft the server now knows about is no longer a draft. Without
      // this the ref keeps it forever, and "is this question unsaved?" —
      // which the deletion payload relies on — answers wrongly for anything
      // the user created and saved in this session.
      draftQuestionsRef.current = draftQuestionsRef.current.filter(
        (draft) => !screeningQuestions.some((serverQ: { id: string }) => serverQ.id === draft.id),
      );
      const combinedQuestions = [...screeningQuestions, ...draftQuestionsRef.current];
      const uniqueQuestions = combinedQuestions.filter(
        (question, index, self) => index === self.findIndex((q) => q.id === question.id),
      );
      // A media upload/delete invalidates the project query, so this effect
      // now also fires mid-edit. Same ownership rule as interview questions
      // (applyServerOwnedMedia): media MEMBERSHIP is server-owned — uploads
      // appear, deletions stay gone — while unsaved label/order edits AND
      // text/answer edits in progress survive the refetch. Previously the
      // no-text-drift branch adopted the server question wholesale, which
      // silently discarded an in-progress label edit on any unrelated
      // refetch (PR #1687 review round 2). TELV1-520
      setQuestions((prevQuestions) =>
        uniqueQuestions.map((serverQuestion) => {
          const localQuestion = prevQuestions.find((q) => q.id === serverQuestion.id);
          if (!localQuestion) return serverQuestion;
          const withMergedMedia = applyServerOwnedMedia(localQuestion, serverQuestion);
          const mergedMedia = 'media' in withMergedMedia ? withMergedMedia.media : undefined;
          const { media: _serverMedia, ...serverRest } = serverQuestion;
          const { media: _localMedia, ...localRest } = localQuestion;
          const base = dequal(serverRest, localRest) ? serverQuestion : localQuestion;
          return mergedMedia === undefined ? base : { ...base, media: mergedMedia as Media[] };
        }),
      );
    }
  }, [screeningQuestions]);

  useEffect(() => {
    setScreeningQuestionsLength(questions?.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions?.length]);

  const isAddScreeningQuestionDisabled = isProjectPublished || isProjectCompleted;
  const isButtonDisabled = isAddScreeningQuestionDisabled || questions?.length >= MAX_QUESTIONS;
  const isSortableDisabled = isUserViewer || isAddScreeningQuestionDisabled;
  const hasQuestions = questions?.length > 0;

  return (
    <div className="flex flex-col flex-grow w-full max-h-full">
      <div className="flex flex w-full justify-between items-center">
        <h4 className="flex items-center gap-1 text-[15px] font-medium">
          {lang.get('msg.screeningQuestions')}
        </h4>
        <ToolTip
          text={lang.get('msg.screeningQuestionsTooltipText')}
          className="max-w-lg border-[3px] border-primary text-sm z-100"
        />
      </div>

      {/*
        Translation guidance — author screeners in the project's Primary
        language; respondent-side translation will localise them at runtime.
        Brand names / proper nouns wrapped in {{double braces}} are preserved
        verbatim so the translator never mangles them (a brand name that also
        reads as a common word would otherwise be translated literally).
        Anchored above the question list so it's impossible to miss before
        adding a question.
      */}
      <div
        role="note"
        className="mt-3 flex items-start gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2.5 text-[13px] text-neutral-700"
      >
        <IconInfoCircle className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
        <span>{lang.get('msg.screenerLanguageNotice')}</span>
      </div>

      {/* questions area */}
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
        onDragStart={() => setIsActiveDrag(true)}
      >
        <div className="flex flex-col w-full h-full gap-5 p-6 my-5 rounded-md bg-slate-50">
          {!hasQuestions && (
            <p className="text-sm font-medium">{lang.get('msg.noScreeningQuestions')}</p>
          )}

          <SortableContext
            disabled={isSortableDisabled}
            items={questions}
            strategy={verticalListSortingStrategy}
          >
            {questions?.map((question, index) => {
              const responses =
                question.type === ScreeningQuestionType.NUMERIC
                  ? question.numericResponse
                  : question.responses;
              return (
                <ScreeningQuestionBox
                  key={question.id}
                  error={errors?.screening_questions?.[index]}
                  index={index}
                  isActiveDrag={isActiveDrag}
                  id={question.id}
                  type={question.type}
                  randomize={question?.randomize}
                  onRandomizeAnswersChange={onRandomizeAnswersChange}
                  question_text={question.question}
                  responses={responses}
                  openQuestionId={openQuestionId}
                  isProjectCompleted={isProjectCompleted}
                  isProjectPublished={isProjectPublished}
                  isUserViewer={isUserViewer}
                  register={register}
                  setOpenQuestionId={setOpenQuestionId}
                  handleDeleteQuestion={handleDeleteQuestion}
                  handleQuestionTextChange={handleQuestionTextChange}
                  handleQuestionTypeChange={handleQuestionTypeChange}
                  handleQuestionAnswerChange={handleQuestionAnswerChange}
                  clearAnswerError={clearAnswerError}
                  handleDeleteAnswer={handleDeleteAnswer}
                  handleAnswersOrderChange={handleAnswersOrderChange}
                  quotasEnabled={question.quotas_enabled}
                  onQuotasEnabledChange={onQuotasEnabledChange}
                  onQuotaPercentageChange={onQuotaPercentageChange}
                  currentQuestion={question}
                  onMediaChange={handleQuestionMediaChange}
                  isQuestionSaved={
                    !!screeningQuestions?.some((dbQuestion) => dbQuestion.id === question.id)
                  }
                />
              );
            })}
          </SortableContext>
        </div>
      </DndContext>
      <Button
        onClick={handleAddNewQuestion}
        disabled={isButtonDisabled}
        variant="default"
        type="button"
        className="mb-2"
      >
        <IconPlus size={16} className="mr-1" />
        {lang.get('msg.addScreeningQuestion')}
      </Button>
    </div>
  );
}

export default ScreeningQuestions;
